/**
 * Lightweight in-memory query cache with TTL support.
 *
 * Intended for GET requests that are repeated across navigations (e.g. the
 * children list, task lists, subscription badge).  Keeps the UI snappy on
 * tab switches without adding a full client-side state-management library.
 *
 * Usage:
 *   import { queryCache } from './queryCache';
 *
 *   // Read (returns undefined on miss or stale)
 *   const cached = queryCache.get('children');
 *
 *   // Write with a TTL (milliseconds).  Default TTL = 2 minutes.
 *   queryCache.set('children', data, 2 * 60 * 1000);
 *
 *   // Invalidate on mutation
 *   queryCache.invalidate('children');
 *
 *   // Invalidate all keys that start with a prefix
 *   queryCache.invalidatePrefix('tasks/');
 */

const DEFAULT_TTL_MS = 2 * 60 * 1000; // 2 minutes

class QueryCache {
  constructor() {
    this._store = new Map(); // key → { value, expiresAt }
  }

  get(key) {
    const entry = this._store.get(key);
    if (!entry) return undefined;
    if (Date.now() > entry.expiresAt) {
      this._store.delete(key);
      return undefined;
    }
    return entry.value;
  }

  set(key, value, ttlMs = DEFAULT_TTL_MS) {
    this._store.set(key, { value, expiresAt: Date.now() + ttlMs });
  }

  invalidate(key) {
    this._store.delete(key);
  }

  /** Remove all keys that start with `prefix`. */
  invalidatePrefix(prefix) {
    for (const key of this._store.keys()) {
      if (key.startsWith(prefix)) this._store.delete(key);
    }
  }

  clear() {
    this._store.clear();
  }
}

export const queryCache = new QueryCache();

/**
 * Wrap an async data-fetching function with the cache.
 *
 * @param {string}   key        Cache key
 * @param {Function} fetchFn    Async function that returns the data
 * @param {number}   [ttlMs]    Cache TTL in milliseconds (default 2 min)
 * @returns The cached value (if fresh) or the result of fetchFn.
 *
 * Example:
 *   const children = await cachedFetch('children', () => api.get('/children').then(r => r.data));
 */
export async function cachedFetch(key, fetchFn, ttlMs = DEFAULT_TTL_MS) {
  const cached = queryCache.get(key);
  if (cached !== undefined) return cached;

  const data = await fetchFn();
  queryCache.set(key, data, ttlMs);
  return data;
}